import {
  Activity,
  ArrowUpRight,
  Bot,
  Check,
  CircleCheck,
  Clock3,
  FileCheck2,
  Gauge,
  GitBranch,
  Hand,
  MessageSquareText,
  QrCode,
  Route,
  ShieldCheck,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { QrPreview } from "./QrPreview";

export function LandingControlDemo() {
  const [published, setPublished] = useState(false);
  const [mobileDeck, setMobileDeck] = useState(() =>
    window.matchMedia("(max-width: 760px)").matches,
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [autoPaused, setAutoPaused] = useState(false);
  const dragStartX = useRef<number | null>(null);
  const dragCurrentX = useRef(0);
  const pauseTimer = useRef<number | null>(null);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 760px)");
    const updateMode = () => setMobileDeck(media.matches);
    updateMode();
    media.addEventListener("change", updateMode);
    return () => media.removeEventListener("change", updateMode);
  }, []);

  useEffect(() => {
    if (
      !mobileDeck ||
      autoPaused ||
      dragging ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    const timer = window.setTimeout(() => {
      setActiveIndex((current) => (current + 1) % 3);
    }, 4200);
    return () => window.clearTimeout(timer);
  }, [activeIndex, autoPaused, dragging, mobileDeck]);

  useEffect(
    () => () => {
      if (pauseTimer.current) window.clearTimeout(pauseTimer.current);
    },
    [],
  );

  const pauseAutoPlay = () => {
    setAutoPaused(true);
    if (pauseTimer.current) window.clearTimeout(pauseTimer.current);
    pauseTimer.current = window.setTimeout(() => setAutoPaused(false), 7000);
  };

  const cardPosition = (index: number) => {
    const offset = (index - activeIndex + 3) % 3;
    if (offset === 0) return "active";
    return offset === 1 ? "next" : "previous";
  };

  const cardClass = (baseClass: string, index: number) =>
    mobileDeck
      ? `demo-phone ${baseClass} is-${cardPosition(index)}`
      : `demo-phone ${baseClass}`;

  const finishDrag = () => {
    if (!dragging) return;
    if (Math.abs(dragCurrentX.current) > 48) {
      setActiveIndex((current) =>
        dragCurrentX.current < 0 ? (current + 1) % 3 : (current + 2) % 3,
      );
    }
    dragStartX.current = null;
    dragCurrentX.current = 0;
    setDragX(0);
    setDragging(false);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest("button, a, input")) return;
    dragStartX.current = event.clientX;
    dragCurrentX.current = 0;
    setDragging(true);
    pauseAutoPlay();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragStartX.current === null) return;
    const nextDrag = event.clientX - dragStartX.current;
    dragCurrentX.current = Math.max(-140, Math.min(140, nextDrag));
    setDragX(dragCurrentX.current);
  };

  const selectCard = (index: number) => {
    pauseAutoPlay();
    setActiveIndex(index);
  };

  const activeLabel = ["创建", "治理", "运行"][activeIndex];

  return (
    <section
      className={
        mobileDeck
          ? published
            ? "mobile-showcase swipe-deck is-published"
            : "mobile-showcase swipe-deck"
          : published
            ? "mobile-showcase is-published"
            : "mobile-showcase"
      }
      aria-label={
        mobileDeck
          ? `Qode 移动控制面产品预览，当前为${activeLabel}`
          : "Qode 移动控制面产品预览"
      }
      data-dragging={mobileDeck && dragging ? "true" : "false"}
      style={
        mobileDeck
          ? ({
              "--drag-x": `${dragX}px`,
              "--drag-rotate": `${dragX * 0.035}deg`,
            } as CSSProperties)
          : undefined
      }
      tabIndex={mobileDeck ? 0 : undefined}
      onKeyDown={
        mobileDeck
          ? (event) => {
              if (event.key === "ArrowLeft") {
                selectCard((activeIndex + 2) % 3);
              }
              if (event.key === "ArrowRight") {
                selectCard((activeIndex + 1) % 3);
              }
            }
          : undefined
      }
      onPointerDown={mobileDeck ? handlePointerDown : undefined}
      onPointerMove={mobileDeck ? handlePointerMove : undefined}
      onPointerUp={mobileDeck ? finishDrag : undefined}
      onPointerCancel={mobileDeck ? finishDrag : undefined}
    >
      <article
        className={cardClass("demo-phone-agent", 0)}
        aria-hidden={mobileDeck ? activeIndex !== 0 : undefined}
        onClick={() => mobileDeck && activeIndex !== 0 && selectCard(0)}
      >
        <div className="demo-phone-frame">
          <span className="demo-phone-speaker" />
          <div className="demo-phone-screen">
            <header className="demo-mobile-header">
              <span className="demo-mobile-mark">
                <QrCode size={14} />
              </span>
              <div>
                <small>Weme Lab</small>
                <strong>Qode Agent</strong>
              </div>
              <button
                type="button"
                aria-label="打开 Agent"
                tabIndex={!mobileDeck || activeIndex === 0 ? 0 : -1}
              >
                <ArrowUpRight size={14} />
              </button>
            </header>

            <div className="demo-mobile-section-title">
              <span>01 · Agent</span>
              <h2>描述你要运行的入口</h2>
            </div>

            <div className="demo-mobile-prompt">
              <MessageSquareText size={14} />
              <span>为夏季门店创建一个可持续更新的二维码</span>
            </div>

            <div className="demo-agent-response">
              <span className="demo-agent-avatar">
                <Bot size={15} />
              </span>
              <div>
                <small>Qode Agent</small>
                <strong>目标与短路径已验证</strong>
                <p>二维码可长期印刷，后续目标更新不需要重新制作物料。</p>
              </div>
            </div>

            <ol className="demo-mobile-checks">
              <li>
                <span>
                  <Check size={11} />
                </span>
                <div>
                  <strong>理解运行目标</strong>
                  <small>活动报名与线下门店</small>
                </div>
              </li>
              <li>
                <span>
                  <Check size={11} />
                </span>
                <div>
                  <strong>验证目标地址</strong>
                  <small>weme.im/summer</small>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>生成 QodeVersion</strong>
                  <small>准备进入治理确认</small>
                </div>
              </li>
            </ol>

            <div className="demo-mobile-bottom-state">
              <Clock3 size={13} />
              <span>计划已生成</span>
              <strong>Ready</strong>
            </div>
          </div>
        </div>
        <footer>
          <strong>创建</strong>
          <span>自然语言生成运行计划</span>
        </footer>
      </article>

      <article
        className={cardClass("demo-phone-governance", 1)}
        aria-hidden={mobileDeck ? activeIndex !== 1 : undefined}
        onClick={() => mobileDeck && activeIndex !== 1 && selectCard(1)}
      >
        <div className="demo-phone-frame">
          <span className="demo-phone-speaker" />
          <div className="demo-phone-screen">
            <header className="demo-mobile-header">
              <span className="demo-mobile-mark demo-mobile-mark-blue">
                <GitBranch size={14} />
              </span>
              <div>
                <small>QodeVersion</small>
                <strong>summer-launch</strong>
              </div>
              <span className="demo-version-label">v1</span>
            </header>

            <div className="demo-mobile-section-title">
              <span>02 · Governance</span>
              <h2>{published ? "版本已经安全发布" : "确认现实影响操作"}</h2>
            </div>

            <div className="demo-version-flow">
              {["Draft", "Validated", "Ready", "Active"].map((item, index) => {
                const active = published ? index <= 3 : index <= 2;
                return (
                  <div className={active ? "is-complete" : ""} key={item}>
                    <span>{active ? <Check size={10} /> : index + 1}</span>
                    <small>{item}</small>
                  </div>
                );
              })}
            </div>

            <dl className="demo-governance-details">
              <div>
                <dt>
                  <Route size={13} />
                  短路径
                </dt>
                <dd>/r/summer-launch</dd>
              </div>
              <div>
                <dt>
                  <FileCheck2 size={13} />
                  目标
                </dt>
                <dd>weme.im/summer</dd>
              </div>
              <div>
                <dt>
                  <ShieldCheck size={13} />
                  策略
                </dt>
                <dd>Owner approval</dd>
              </div>
            </dl>

            <div className="demo-impact-notice">
              <ShieldCheck size={16} />
              <span>
                <strong>{published ? "治理记录已写入" : "需要你的确认"}</strong>
                <small>
                  {published
                    ? "操作可审计、可回退"
                    : "现实影响操作不会自动执行"}
                </small>
              </span>
            </div>

            <button
              className={published ? "demo-publish-button is-published" : "demo-publish-button"}
              type="button"
              disabled={published}
              tabIndex={!mobileDeck || activeIndex === 1 ? 0 : -1}
              onClick={() => {
                setPublished(true);
                pauseAutoPlay();
                if (mobileDeck) setActiveIndex(2);
              }}
            >
              {published ? (
                <>
                  <CircleCheck size={15} />
                  已发布
                </>
              ) : (
                <>
                  确认发布
                  <ArrowUpRight size={14} />
                </>
              )}
            </button>

            <div className="demo-mobile-audit">
              <span />
              审批、版本与操作者已进入审计时间线
            </div>
          </div>
        </div>
        <footer>
          <strong>治理</strong>
          <span>显式版本与人工确认</span>
        </footer>
      </article>

      <article
        className={cardClass("demo-phone-live", 2)}
        aria-hidden={mobileDeck ? activeIndex !== 2 : undefined}
        onClick={() => mobileDeck && activeIndex !== 2 && selectCard(2)}
      >
        <div className="demo-phone-frame">
          <span className="demo-phone-speaker" />
          <div className="demo-phone-screen">
            <header className="demo-mobile-header">
              <span className="demo-mobile-mark demo-mobile-mark-amber">
                <Activity size={14} />
              </span>
              <div>
                <small>Live Qode</small>
                <strong>/summer-launch</strong>
              </div>
              <span className={published ? "demo-live-label is-live" : "demo-live-label"}>
                {published ? "Active" : "Ready"}
              </span>
            </header>

            <div className="demo-mobile-section-title">
              <span>03 · Operations</span>
              <h2>{published ? "入口正在持续运行" : "等待版本发布"}</h2>
            </div>

            <div className="demo-live-qr">
              <QrPreview
                value="https://qode.weme.im/r/summer-launch"
                size={146}
              />
              <strong>qode.weme.im/r/summer-launch</strong>
              <span>实体二维码始终保持不变</span>
            </div>

            <dl className="demo-live-metrics">
              <div>
                <dt>
                  <Gauge size={13} />
                  解析延迟
                </dt>
                <dd>{published ? "39 ms" : "待发布"}</dd>
              </div>
              <div>
                <dt>
                  <Activity size={13} />
                  累计扫描
                </dt>
                <dd>{published ? "1,842" : "0"}</dd>
              </div>
              <div>
                <dt>
                  <GitBranch size={13} />
                  活动版本
                </dt>
                <dd>{published ? "v1" : "—"}</dd>
              </div>
            </dl>

            <div className={published ? "demo-runtime-state is-live" : "demo-runtime-state"}>
              {published ? <CircleCheck size={14} /> : <Clock3 size={14} />}
              <span>
                <strong>
                  {published ? "Resolver 已完成同步" : "Resolver 等待发布"}
                </strong>
                <small>
                  {published ? "持续健康检查已启动" : "确认后立即进入解析网络"}
                </small>
              </span>
            </div>
          </div>
        </div>
        <footer>
          <strong>运行</strong>
          <span>实时解析、监测与回退</span>
        </footer>
      </article>

      {mobileDeck ? (
        <>
          <div className="swipe-gesture" aria-hidden="true">
            <span />
            <Hand size={15} strokeWidth={1.7} />
          </div>
          <div className="swipe-position" aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <span
                className={index === activeIndex ? "is-active" : ""}
                key={index}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}
